import type { Reviewer } from '@/types';

export interface ProfileReactiveQuestion {
  section: 'About You' | 'Load Boost' | 'Soaking Wet' | 'Cross-Profile' | 'Experience Details';
  field: string;
  getValue: (r: Reviewer) => string | undefined;
  condition: (r: Reviewer) => boolean;
  prompt: string;
  context: string;
}

export const PROFILE_REACTIVE_QUESTIONS: ProfileReactiveQuestion[] = [
  // ─── About You ──────────────────────────────────────────────────────
  {
    section: 'About You',
    field: 'Sexually active',
    getValue: r => r.sexuallyActive,
    condition: r => r.sexuallyActive === 'Very active',
    prompt: "You mentioned being very sexually active - how often would you say that is? Does that influence what kind of content you're drawn to?",
    context: 'Based on sexual activity level',
  },
  {
    section: 'About You',
    field: 'Sexually active',
    getValue: r => r.sexuallyActive,
    condition: r => r.sexuallyActive === 'Fairly active',
    prompt: "You're fairly sexually active - do you feel like your current sex life is where you want it to be, or are there things you'd like to explore more?",
    context: 'Based on sexual activity level',
  },
  {
    section: 'About You',
    field: 'Sexually active',
    getValue: r => r.sexuallyActive,
    condition: r => r.sexuallyActive === 'Occasionally',
    prompt: "You mentioned being occasionally active - is that by choice, or would you like it to be more frequent? What tends to get in the way?",
    context: 'Based on sexual activity level',
  },
  {
    section: 'About You',
    field: 'Sexually active',
    getValue: r => r.sexuallyActive,
    condition: r => r.sexuallyActive === 'Not currently',
    prompt: "You mentioned not being currently sexually active - are you still interested in exploring content and products, or is this more about future plans?",
    context: 'Based on sexual activity level',
  },
  {
    section: 'About You',
    field: 'Watches adult content',
    getValue: r => r.watchesAdultContent,
    condition: r => r.watchesAdultContent === 'No',
    prompt: "You mentioned not watching adult content - what drew you to reviewing? Is there a specific type of content you'd be comfortable starting with?",
    context: 'Based on content viewing habits',
  },
  {
    section: 'About You',
    field: 'Watches adult content',
    getValue: r => r.watchesAdultContent,
    condition: r => r.watchesAdultContent === 'Rarely',
    prompt: "You rarely watch adult content - when you do, what makes you seek it out? Is there a particular style or scenario you prefer?",
    context: 'Based on content viewing habits',
  },
  {
    section: 'About You',
    field: 'Familiar with products',
    getValue: r => r.familiarWithProducts,
    condition: r => r.familiarWithProducts === 'No - new to these',
    prompt: "You're new to these products - what are you most curious about? Would you like us to walk you through what's available?",
    context: 'Based on product familiarity',
  },
  {
    section: 'About You',
    field: 'Relevant experience',
    getValue: r => r.relevantExperience,
    condition: r => !!r.relevantExperience && r.relevantExperience.toLowerCase().includes('adult content'),
    prompt: "You mentioned experience in the adult content industry - what role did you have? That perspective would be really valuable for our reviews.",
    context: 'Based on relevant experience',
  },
  {
    section: 'About You',
    field: 'Relevant experience',
    getValue: r => r.relevantExperience,
    condition: r => !!r.relevantExperience && r.relevantExperience.toLowerCase().includes('content moderation'),
    prompt: "You have content moderation experience - how does that shape what you look for in clips? Do you notice things other reviewers might miss?",
    context: 'Based on relevant experience',
  },

  // ─── Load Boost (Female) ───────────────────────────────────────────
  {
    section: 'Load Boost',
    field: 'Enjoys volume content',
    getValue: r => r.enjoysVolumeContent,
    condition: r => r.enjoysVolumeContent === 'Yes, definitely',
    prompt: "You definitely enjoy volume content - what is it about bigger loads that appeals to you? Visual, sensation, or something else?",
    context: 'About volume content preferences',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys volume content',
    getValue: r => r.enjoysVolumeContent,
    condition: r => r.enjoysVolumeContent === 'Not particularly',
    prompt: "You're not particularly into volume content - is there a specific reason? Would your opinion change if it was part of a scenario you enjoyed?",
    context: 'About volume content preferences',
  },
  {
    section: 'Load Boost',
    field: 'Want partner load boost',
    getValue: r => r.wantPartnerLoadBoost,
    condition: r => r.wantPartnerLoadBoost === 'Yes, definitely',
    prompt: "You'd definitely want your partner to try Load Boost - have you talked to them about it? What would bigger loads mean for your experience together?",
    context: 'About partner using Load Boost',
  },
  {
    section: 'Load Boost',
    field: 'Want partner load boost',
    getValue: r => r.wantPartnerLoadBoost,
    condition: r => r.wantPartnerLoadBoost === 'Maybe, curious',
    prompt: "You're curious about your partner trying Load Boost - what would make you more interested? Would seeing results in content help you decide?",
    context: 'About partner using Load Boost',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys swallowing',
    getValue: r => r.enjoysSwallowing,
    condition: r => r.enjoysSwallowing === 'Yes, usually',
    prompt: "You usually enjoy swallowing - is taste a factor for you? Would a supplement that improved taste make the experience even better?",
    context: 'About swallowing preferences',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys swallowing',
    getValue: r => r.enjoysSwallowing,
    condition: r => r.enjoysSwallowing === 'Sometimes',
    prompt: "You sometimes enjoy swallowing - what makes the difference between times you do and don't? Is it about taste, mood, or something else?",
    context: 'About swallowing preferences',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys swallowing',
    getValue: r => r.enjoysSwallowing,
    condition: r => r.enjoysSwallowing === 'Not typically' || r.enjoysSwallowing === 'Never',
    prompt: "Is this a taste/texture thing, or something else? Would better taste change anything?",
    context: 'About swallowing preferences',
  },
  {
    section: 'Load Boost',
    field: 'Better taste',
    getValue: r => r.wouldEnjoyBetterTaste,
    condition: r => r.wouldEnjoyBetterTaste === 'Yes, definitely',
    prompt: "You'd definitely enjoy better taste - have you or your partner tried anything to improve it? What flavor profiles would be ideal?",
    context: 'About taste preferences',
  },
  {
    section: 'Load Boost',
    field: 'Better taste',
    getValue: r => r.wouldEnjoyBetterTaste,
    condition: r => r.wouldEnjoyBetterTaste === 'It already tastes fine',
    prompt: "Glad taste is already fine! Would you say your partner's diet plays a role? Any tips you'd share with other reviewers?",
    context: 'About taste preferences',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys facials',
    getValue: r => r.enjoysFacials,
    condition: r => r.enjoysFacials === 'Yes',
    prompt: "You enjoy facials - is it the visual aspect, the feeling, or the dynamic? Does volume play a role in how much you enjoy them?",
    context: 'About facial preferences',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys facials',
    getValue: r => r.enjoysFacials,
    condition: r => r.enjoysFacials === 'Sometimes',
    prompt: "You sometimes enjoy facials - what makes it a yes vs. a no in the moment? Would more volume make them more appealing?",
    context: 'About facial preferences',
  },
  {
    section: 'Load Boost',
    field: 'Enjoys facials',
    getValue: r => r.enjoysFacials,
    condition: r => r.enjoysFacials === 'Not typically' || r.enjoysFacials === 'Never tried',
    prompt: "Is this a boundary, or just hasn't come up? Would planned facials change anything?",
    context: 'About facial preferences',
  },
  {
    section: 'Load Boost',
    field: 'Volume makes difference',
    getValue: r => r.volumeMakesDifference,
    condition: r => r.volumeMakesDifference === 'Yes, definitely',
    prompt: "Volume definitely makes a difference for you - in what way? Is it about the visual, the sensation, or something more psychological?",
    context: 'About volume importance',
  },
  {
    section: 'Load Boost',
    field: 'Volume makes difference',
    getValue: r => r.volumeMakesDifference,
    condition: r => r.volumeMakesDifference === 'Not particularly',
    prompt: "Volume doesn't particularly matter to you - what aspects of the sexual experience do you focus on more?",
    context: 'About volume importance',
  },

  // ─── Soaking Wet (Female) ──────────────────────────────────────────
  {
    section: 'Soaking Wet',
    field: 'Can squirt',
    getValue: r => r.canSquirt,
    condition: r => r.canSquirt === 'Yes, easily',
    prompt: "You can squirt easily - that's great! What techniques work best for you? Would you be comfortable sharing tips in reviews?",
    context: 'About squirting ability',
  },
  {
    section: 'Soaking Wet',
    field: 'Can squirt',
    getValue: r => r.canSquirt,
    condition: r => r.canSquirt === 'Sometimes',
    prompt: "You can sometimes squirt - what conditions make it more likely? Is it position, arousal level, or something specific your partner does?",
    context: 'About squirting ability',
  },
  {
    section: 'Soaking Wet',
    field: 'Can squirt',
    getValue: r => r.canSquirt,
    condition: r => r.canSquirt === 'Not yet but would like to',
    prompt: "You'd like to learn to squirt - have you tried any techniques? Would guided product recommendations help you on that journey?",
    context: 'About squirting ability',
  },
  {
    section: 'Soaking Wet',
    field: 'Can squirt',
    getValue: r => r.canSquirt,
    condition: r => r.canSquirt === 'Not interested',
    prompt: "Could you share why you're not interested? Is it something you've thought about before?",
    context: 'Based on your squirting response',
  },
  {
    section: 'Soaking Wet',
    field: 'Want to squirt more',
    getValue: r => r.wantToSquirtMore,
    condition: r => r.wantToSquirtMore === 'Yes, definitely',
    prompt: "You definitely want to squirt more - what's holding you back? Would trying Soaking Wet during solo play help you practice?",
    context: 'About squirting goals',
  },
  {
    section: 'Soaking Wet',
    field: 'Want to squirt more',
    getValue: r => r.wantToSquirtMore,
    condition: r => r.wantToSquirtMore === 'Maybe, curious',
    prompt: "You're curious about squirting more - what would make you want to explore it further? Would watching content about it help?",
    context: 'About squirting goals',
  },
  {
    section: 'Soaking Wet',
    field: 'Used arousal products',
    getValue: r => r.usedArousalProducts,
    condition: r => r.usedArousalProducts === 'Yes - Soaking Wet',
    prompt: "You've already used Soaking Wet - what was your experience like? Did you notice a difference in wetness or arousal?",
    context: 'About product experience',
  },
  {
    section: 'Soaking Wet',
    field: 'Used arousal products',
    getValue: r => r.usedArousalProducts,
    condition: r => r.usedArousalProducts === 'No but interested',
    prompt: "You're interested in trying arousal products - what's stopped you so far? Would a guided first-time experience help?",
    context: 'About product experience',
  },
  {
    section: 'Soaking Wet',
    field: 'Orgasm from sex',
    getValue: r => r.orgasmFromSex,
    condition: r => r.orgasmFromSex === 'Always' || r.orgasmFromSex === 'Usually',
    prompt: "You consistently orgasm from sex - that's wonderful! What positions or techniques work best? Your insights would be valuable for our content recommendations.",
    context: 'About orgasm experience',
  },
  {
    section: 'Soaking Wet',
    field: 'Orgasm from sex',
    getValue: r => r.orgasmFromSex,
    condition: r => r.orgasmFromSex === 'Sometimes',
    prompt: "You sometimes orgasm from sex - what tends to make the difference? Would content focused on techniques that work for you be helpful?",
    context: 'About orgasm experience',
  },
  {
    section: 'Soaking Wet',
    field: 'Orgasm from sex',
    getValue: r => r.orgasmFromSex,
    condition: r => r.orgasmFromSex === 'Rarely' || r.orgasmFromSex === 'Never',
    prompt: "Do you orgasm in other ways? What tends to work better for you?",
    context: 'About your orgasm experience',
  },
  {
    section: 'Soaking Wet',
    field: 'Typical wetness',
    getValue: r => r.typicalWetness != null ? `${r.typicalWetness}/5` : undefined,
    condition: r => r.typicalWetness != null && r.typicalWetness >= 4,
    prompt: "You rate your wetness quite high - does that fluctuate with your cycle or arousal level? Do you think products could enhance it further?",
    context: 'About wetness levels',
  },
  {
    section: 'Soaking Wet',
    field: 'Typical wetness',
    getValue: r => r.typicalWetness != null ? `${r.typicalWetness}/5` : undefined,
    condition: r => r.typicalWetness != null && r.typicalWetness <= 2,
    prompt: "Would being wetter enhance your experience? Have you tried any products?",
    context: 'About wetness levels',
  },

  // ─── Cross-Profile (smart combos) ─────────────────────────────────
  {
    section: 'Cross-Profile',
    field: 'High wetness + private on squirting',
    getValue: r => `Wetness: ${r.typicalWetness}/5, Squirting: ${r.canSquirt}`,
    condition: r =>
      r.typicalWetness != null && r.typicalWetness >= 4 &&
      r.canSquirt === 'Prefer not to say',
    prompt: "Your wetness is really high which is great! Some women with similar levels find they're closer to squirting than they think. No pressure at all, but would you be open to exploring that?",
    context: 'Cross-profile insight',
  },
  {
    section: 'Cross-Profile',
    field: 'High wetness + wants to squirt',
    getValue: r => `Wetness: ${r.typicalWetness}/5, Wants more: ${r.wantToSquirtMore}`,
    condition: r =>
      r.typicalWetness != null && r.typicalWetness >= 4 &&
      (r.wantToSquirtMore === 'Yes, definitely' || r.wantToSquirtMore === 'Maybe, curious'),
    prompt: "With your naturally high wetness and interest in squirting, you're in a great position to explore! Have you tried G-spot focused techniques? Soaking Wet could help bridge the gap.",
    context: 'Cross-profile encouragement',
  },
  {
    section: 'Cross-Profile',
    field: 'Swallowing + better taste',
    getValue: r => `Swallowing: ${r.enjoysSwallowing}, Taste: ${r.wouldEnjoyBetterTaste}`,
    condition: r =>
      (r.enjoysSwallowing === 'Yes, usually' || r.enjoysSwallowing === 'Sometimes') &&
      r.wouldEnjoyBetterTaste === 'Yes, definitely',
    prompt: "Since you enjoy swallowing but want better taste, Load Boost could be a game-changer for you. It's designed to improve both volume and taste. Would your partner be open to trying it?",
    context: 'Cross-profile product match',
  },
  {
    section: 'Cross-Profile',
    field: 'Volume content + volume matters',
    getValue: r => `Enjoys volume content: ${r.enjoysVolumeContent}, Volume matters: ${r.volumeMakesDifference}`,
    condition: r =>
      r.enjoysVolumeContent === 'Yes, definitely' &&
      r.volumeMakesDifference === 'Yes, definitely',
    prompt: "You love volume content AND find volume makes a difference in real life - you'd be a perfect reviewer for our Load Boost clips! Would you be interested in focusing on those?",
    context: 'Cross-profile reviewer match',
  },
  {
    section: 'Cross-Profile',
    field: 'Low orgasm + low wetness',
    getValue: r => `Orgasm: ${r.orgasmFromSex}, Wetness: ${r.typicalWetness}/5`,
    condition: r =>
      (r.orgasmFromSex === 'Rarely' || r.orgasmFromSex === 'Never') &&
      r.typicalWetness != null && r.typicalWetness <= 2,
    prompt: "Low wetness and difficulty orgasming often go together - arousal products like Soaking Wet can help with both by increasing blood flow and sensitivity. Would you be interested in trying it?",
    context: 'Cross-profile product suggestion',
  },

  // ─── Experience Details (text fields) ──────────────────────────────
  {
    section: 'Experience Details',
    field: 'Fantasy',
    getValue: r => r.fantasiesNotTried ? r.fantasiesNotTried.slice(0, 40) + '...' : undefined,
    condition: r => !!r.fantasiesNotTried && r.fantasiesNotTried.trim().length > 0,
    prompt: '', // Dynamic - set in getTriggeredQuestions
    context: 'About your untried fantasy',
  },
  {
    section: 'Experience Details',
    field: 'Favorite memory',
    getValue: r => r.favoriteSexualMemory ? 'shared' : undefined,
    condition: r => !!r.favoriteSexualMemory && r.favoriteSexualMemory.trim().length > 0,
    prompt: "That sounds like a great memory! Would you want to recreate something similar? Are there clips that remind you of it?",
    context: 'Based on your favorite memory',
  },
  {
    section: 'Experience Details',
    field: 'Turn-ons',
    getValue: r => r.biggestTurnOns ? r.biggestTurnOns.slice(0, 40) + '...' : undefined,
    condition: r => !!r.biggestTurnOns && r.biggestTurnOns.trim().length > 0,
    prompt: "Thanks for sharing your turn-ons! Do any specific clip categories match these? Want personalized recommendations?",
    context: 'Based on your turn-ons',
  },
  {
    section: 'Experience Details',
    field: 'Planned facials',
    getValue: () => 'prefers planned',
    condition: r => !!r.facialExperience && r.facialExperience.toLowerCase().includes('planned'),
    prompt: "You mentioned liking planned facials - what makes planning better? How does your partner feel about it?",
    context: 'Based on your facial experience',
  },
  {
    section: 'Experience Details',
    field: 'Anal',
    getValue: () => 'not interested',
    condition: r =>
      !!r.analExperience &&
      (r.analExperience.toLowerCase().includes('no') || r.analExperience.toLowerCase().includes('never')),
    prompt: "Is anal something you'd consider under certain circumstances, or is it a firm boundary?",
    context: 'About your anal experience response',
  },

  // ─── Male-specific ─────────────────────────────────────────────────
  {
    section: 'Load Boost',
    field: 'Squirting content',
    getValue: r => r.enjoysSquirtingContent,
    condition: r => r.enjoysSquirtingContent === 'Not particularly',
    prompt: "What type of content do you prefer? What makes squirting content less appealing?",
    context: 'About content preferences',
  },
];

export interface TriggeredQuestion {
  section: string;
  field: string;
  value: string;
  prompt: string;
  context: string;
}

/**
 * Evaluates a reviewer's profile against all reactive question conditions
 * and returns the triggered questions with resolved values.
 */
export function getTriggeredQuestions(
  reviewer: Reviewer,
  alreadySentQuestions: string[]
): TriggeredQuestion[] {
  const results: TriggeredQuestion[] = [];

  for (const q of PROFILE_REACTIVE_QUESTIONS) {
    if (!q.condition(reviewer)) continue;

    const value = q.getValue(reviewer);
    if (!value) continue;

    // Build the prompt - handle dynamic text field prompts
    let prompt = q.prompt;
    if (q.field === 'Fantasy' && reviewer.fantasiesNotTried) {
      prompt = `You mentioned wanting to try "${reviewer.fantasiesNotTried.slice(0, 50)}${reviewer.fantasiesNotTried.length > 50 ? '...' : ''}" - what's stopped you so far? Would you like clip recommendations for this?`;
    }

    // Skip if already sent (match on first 30 chars of prompt)
    if (alreadySentQuestions.some(sent => sent.includes(prompt.slice(0, 30)))) continue;

    results.push({
      section: q.section,
      field: q.field,
      value,
      prompt,
      context: q.context,
    });
  }

  return results;
}
